<template>
  <div class="page">
    <v-container fluid>
      <v-form ref="form">
        <v-row class="d-flex justify-center mt-3">
          <v-col cols="3">
            <v-text-field
              label="Nome da Turma"
              v-model="form.filters.nome"
              outlined
              dense
              @keyup.enter="buscarTurmas()"
            ></v-text-field>
          </v-col>

          <v-col cols="3">
            <v-select
              v-model="form.filters.municipio"
              label="Município"
              item-text="nome"
              item-value="id"
              outlined
              return-object
              dense
              :items="form.lists.listMunicipiosFilter"
              @change="filterEscolasByMunicipios"
            ></v-select>
          </v-col>

          <v-col cols="3">
            <v-text-field
              label="Professor"
              v-model="form.filters.professor"
              outlined
              dense
              maxlength="70"
              @keyup.enter="buscarTurmas()"
            ></v-text-field>
          </v-col>

          <v-col cols="3">
            <v-text-field
              label="Ano Letivo"
              v-model="form.filters.anoLetivo"
              outlined
              dense
              v-only-number
              maxlength="4"
              @keyup.enter="buscarTurmas()"
              :rules="[
                (v) => !v || (v >= 1900 && v <= new Date().getFullYear() + 1) || 'Ano Letivo inválido.'
              ]"
            ></v-text-field>
          </v-col>

          <v-col cols="3">
            <v-select
              v-model="form.filters.serieId"
              label="Ano/Série"
              item-text="nome"
              item-value="id"
              outlined
              dense
              :items="form.lists.listAnoSeries"
            ></v-select>
          </v-col>

          <v-col cols="3">
            <v-select
              v-model="form.filters.escola"
              label="Escola"
              item-text="local"
              item-value="id"
              outlined
              return-object
              dense
              :items="form.lists.listEscolasFilter"
              :loading="loadingEscolas"
              @focus="ensureEscolas"
              @change="filterMunicipiosByEscola"
            ></v-select>
          </v-col>

          <sca-select-ativo
            :cols="3"
            :config="{ labelAtivo: 'Ativo', labelInativo: 'Inativo', label: 'Situação' }"
            v-model="form.filters.ativo"
          ></sca-select-ativo>
        </v-row>

        <div class="text-center">
          <v-col cols="12">
            <sca-tooltip
              :text="'Buscar'"
              :icon="'mdi-magnify'"
              @click="buscarTurmas()"
            ></sca-tooltip>
            <sca-tooltip
              :text="'Limpar'"
              :icon="'mdi-eraser'"
              @click="resetForm()"
            ></sca-tooltip>
            <sca-tooltip
              :text="'Adicionar'"
              :icon="'mdi-plus'"
              @click="openDialogManterTurma()"
            ></sca-tooltip>
          </v-col>
        </div>
      </v-form>

      <v-data-table
        class="mt-5"
        :headers="table.headers"
        :items="table.itens"
        :items-per-page="10"
        :footer-props="$footerProps"
        show-expand
        :no-data-text="table.noDataText"
        :expanded.sync="expanded"
      >
        <template v-slot:expanded-item="{ headers, item }">
          <td class="permissoes" :colspan="headers.length">
            <v-chip-group active-class="primary--text" column>
              <v-chip class="chip-permissao" v-for="(prof, i) in item.professores" :key="i">
                {{ labelProfessor[i] }}&nbsp;<b>{{ prof }}</b>
              </v-chip>
            </v-chip-group>
          </td>
        </template>

        <template v-slot:[`item.situacao`]="{ item }">
          <sca-label-ativo
            :ativo="item.ativo"
            :config="{ labelAtivo: 'Ativo', labelInativo: 'Inativo' }"
          ></sca-label-ativo>
        </template>

        <template v-slot:[`item.acoes`]="{ item }">
          <sca-tooltip
            :text="'Visualizar'"
            :icon="'mdi-eye-outline'"
            :small="true"
            @click="openDialogVisualizarTurma(item)"
          ></sca-tooltip>
          <sca-tooltip
            :text="'Editar'"
            :icon="'mdi-pencil'"
            :small="true"
            @click="openDialogManterTurma(item)"
          ></sca-tooltip>
          <sca-tooltip
            :text="`${item.situacao ? 'Inativar' : 'Ativar'}`"
            :icon="`${item.situacao ? 'mdi-toggle-switch-off' : 'mdi-toggle-switch'}`"
            :small="true"
            @click="inativarAtivarTurma(item)"
          ></sca-tooltip>
        </template>
      </v-data-table>
    </v-container>

    <CadastrarEditarTurmaDialog
      :visible="dialogManterTurma.visible"
      :content="dialogManterTurma.content"
      :edit="dialogManterTurma.edit"
      :lists="form.lists"
      @onClose="dialogManterTurma.close()"
      @onSave="buscarTurmas()"
    ></CadastrarEditarTurmaDialog>

    <VisualizarTurmaDialog
      v-if="dialogVisualizarTurma.visible"
      :visible="dialogVisualizarTurma.visible"
      :content="dialogVisualizarTurma.content"
      :lists="form.lists"
      @onClose="dialogVisualizarTurma.close()"
    ></VisualizarTurmaDialog>
  </div>
</template>

<script>
import { Dialog } from '~/commons/classes/dialog';
import TurmaService from '~/services/turma.service';
import datatablesMixin from '~/mixins/datatables.mixin';
import CadastrarEditarTurmaDialog from './dialogs/CadastrarEditarTurmaDialog.vue';
import VisualizarTurmaDialog from './dialogs/VisualizarTurmaDialog.vue';
import { Turma } from '~/commons/classes/turma';
import PreLoginService from '~/services/pre-login.service';
import AnoLetivoService from '~/services/ano-letivo.service';
import SerieService from '~/services/serie.service';
import LotacaoService from '~/services/lotacao.service';
import TurnoService from '~/services/turno.service';
import ProfissionalService from '~/services/profissional.service';

export default {
  name: 'ManterTurmaView',
  mixins: [
    TurmaService,
    datatablesMixin,
    PreLoginService,
    AnoLetivoService,
    SerieService,
    LotacaoService,
    TurnoService,
    ProfissionalService
  ],
  components: {
    CadastrarEditarTurmaDialog,
    VisualizarTurmaDialog
  },
  data: () => ({
    dialogManterTurma: Dialog.Build(),
    dialogVisualizarTurma: Dialog.Build(),
    labelProfessor: ['Professor(a) de Língua Portuguesa:', 'Professor(a) de Matemática:'],
    loading: false,
    loadingEscolas: false,
    loadingProfessores: false,
    form: {
      filters: {
        nome: null,
        municipio: null,
        professor: null,
        serieId: null,
        escola: null,
        ativo: null,
        anoLetivo: null
      },
      lists: {
        listMunicipios: [],
        listAnoSeries: [],
        listEscolas: [],
        listAnosLetivos: [],
        listTurnos: [],
        listProfessor: [],
        listMunicipiosFilter: [],
        listEscolasFilter: []
      }
    },
    expanded: [],
    table: {
      headers: [
        { text: 'Nome da Turma', align: 'center', value: 'nome' },
        { text: 'Ano/Série', align: 'center', value: 'serie' },
        { text: 'Município', align: 'center', value: 'municipio' },
        { text: 'Escola', align: 'center', value: 'escola' },
        { text: 'Professor', align: 'center', value: 'data-table-expand' },
        { text: 'Situação', align: 'center', value: 'situacao', sortable: false },
        { text: 'Ano Letivo', align: 'center', value: 'ano' },
        { text: 'Ações', align: 'center', value: 'acoes', sortable: false }
      ],
      itens: [],
      noDataText: ''
    }
  }),
  methods: {
    async fetchWithRetry(fn, { retries = 2, delay = 300 } = {}) {
      try {
        return await fn();
      } catch (e) {
        if (retries > 0) {
          await new Promise((r) => setTimeout(r, delay));
          return this.fetchWithRetry(fn, { retries: retries - 1, delay: delay * 2 });
        }
        throw e;
      }
    },

    iniciarMensagemDeNenhumRegistroEncontrado() {
      this.table.noDataText = this.$possuiPermissao('turma.listar')
        ? ''
        : 'Não há registros a serem exibidos';
    },

    async initBootstrap() {
      this.loading = true;
      this.iniciarMensagemDeNenhumRegistroEncontrado();
      try {
        const [
          { data: municipios },
          { data: series },
          { data: anos },
          { data: turnos }
        ] = await Promise.all([
          this.fetchWithRetry(() => this.$obterMunicipios()),
          this.fetchWithRetry(() => this.$buscarSeries({ params: { ativo: true } })),
          this.fetchWithRetry(() => this.$buscarAnosLetivos({ params: { ativo: true } })),
          this.fetchWithRetry(() => this.$buscarTurnos({ params: { ativo: true } }))
        ]);

        this.form.lists.listMunicipios = municipios?.lista || [];
        this.form.lists.listMunicipiosFilter = municipios?.lista || [];
        this.form.lists.listAnoSeries = series?.itens || [];
        this.form.lists.listAnosLetivos = anos?.itens || [];
        this.form.lists.listTurnos = turnos?.itens || [];
      } catch (err) {
        this.$notifyError('Falha ao carregar dados iniciais. Tente novamente.');
      } finally {
        this.loading = false;
      }
    },

    async ensureEscolas() {
      if (this.loadingEscolas || (this.form.lists.listEscolas?.length || 0) > 0) return;
      this.loadingEscolas = true;
      try {
        const { data } = await this.fetchWithRetry(() =>
          this.$buscarLotacoes({ params: { ativo: true, escola: true } })
        );
        const lista = data?.resposta || [];
        this.form.lists.listEscolas = lista;
        this.form.lists.listEscolasFilter = lista;
      } catch (err) {
        this.$notifyError('Não foi possível carregar as escolas.');
      } finally {
        this.loadingEscolas = false;
      }
    },

    async ensureProfessores() {
      if (this.loadingProfessores || (this.form.lists.listProfessor?.length || 0) > 0) return;
      this.loadingProfessores = true;
      try {
        const { data } = await this.fetchWithRetry(() =>
          this.$buscarProfissionais({ params: { ativo: true, funcao: 'Professor' } })
        );
        this.form.lists.listProfessor = data?.itens || [];
      } catch (err) {
        this.$notifyError('Não foi possível carregar os professores.');
      } finally {
        this.loadingProfessores = false;
      }
    },

    buscarTurmas() {
      if (this.$validarFormulario()) {
        this.$buscarTurmas({ params: this.getParams() })
          .then(({ data }) => {
            this.table.itens = data.itens;
            this.table.noDataText = data.itens.length === 0 ? 'Não há registros a serem exibidos.' : '';
          })
          .catch(() => this.$notifyError('Erro ao buscar turmas.'));
      }
    },

    getParams() {
      const { municipio, escola, serieId, anoLetivo, professor, ativo, nome } = this.form.filters;
      return {
        municipioId: municipio?.id || null,
        escolaId: escola?.id || null,
        serieId,
        anoLetivo,
        professor,
        ativo,
        nome,
        ano: anoLetivo
      };
    },

    openDialogManterTurma(turma) {
      this.ensureProfessores();
      this.ensureEscolas();

      turma
        ? this.$visualizarTurmas(turma).then(({ data }) =>
          this.dialogManterTurma.open(new Turma(data.resposta), true)
        )
        : this.dialogManterTurma.open(Turma.Build());
    },

    openDialogVisualizarTurma(turma) {
      this.ensureEscolas();
      this.ensureProfessores();

      this.$visualizarTurmas(turma).then(({ data }) =>
        this.dialogVisualizarTurma.open(new Turma(data.resposta))
      );
    },

    inativarAtivarTurma(turma) {
      this.$swal({
        text: `Deseja ${turma.ativo ? 'inativar' : 'ativar'} o registro?`,
        type: 'warning',
        showCancelButton: true,
        confirmButtonText: `Confirmar`,
        cancelButtonText: `Cancelar`
      }).then(() => {
        this.$inativarAtivarTurma(turma.id)
          .then(({ data }) => {
            this.$notifySuccess(data.mensagem);
            this.buscarTurmas();
          })
          .catch(() => this.$notifyError('Falha ao alterar situação.'));
      });
    },

    resetForm() {
      this.$resetForm();
      this.table.itens = [];
      this.expanded = [];
      this.form.filters.ativo = null;
      this.form.lists.listMunicipiosFilter = this.form.lists.listMunicipios;
      this.form.lists.listEscolasFilter = this.form.lists.listEscolas;
    },

    filterMunicipiosByEscola() {
      this.form.lists.listMunicipiosFilter = this.form.lists.listMunicipios.filter(
        (municipio) => municipio.nome === this.form.filters.escola?.municipio
      );
    },

    filterEscolasByMunicipios() {
      this.form.lists.listEscolasFilter = this.form.lists.listEscolas.filter(
        (escola) => escola.municipio === this.form.filters.municipio?.nome
      );
    }
  },

  mounted() {
    this.initBootstrap();
  }
};
</script>
